import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Activity, Sparkles, AlertTriangle, CheckCircle2, Loader2,
  FolderOpen, Film, Scissors, Clock, Upload, LogIn, Cpu,
  BarChart3, RefreshCw,
} from 'lucide-react'
import apiClient from '../services/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreditsUsage {
  credits_mode: string
  used_credits: number
  max_credits: number | null
  remaining_credits: number | null
  unlimited: boolean
  ai_drafts_total: number
  ai_drafts_last_7d: number
  ai_drafts_generating: number
  ai_drafts_ready: number
  ai_drafts_failed: number
}

interface DashboardData {
  period_days: number
  is_admin?: boolean
  counts: {
    projects: number
    assets: number
    moments: number
    assemblies: number
    total_footage_seconds: number
  }
  activity_by_type: Record<string, number>
  activity_per_day: { day: string; count: number }[]
  activity_feed: {
    id: number
    type: string
    description: string
    created_at: string | null
    user_email?: string
  }[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

function formatRelativeTime(isoStr: string | null): string {
  if (!isoStr) return ''
  const diff = Date.now() - new Date(isoStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

const ACTIVITY_ICONS: Record<string, { icon: typeof Activity; color: string }> = {
  login: { icon: LogIn, color: 'text-emerald-400' },
  logout: { icon: LogIn, color: 'text-[var(--rq-text-soft)]' },
  upload: { icon: Upload, color: 'text-[var(--rq-blue)]' },
  analysis: { icon: Cpu, color: 'text-[var(--rq-amber)]' },
  asset_create: { icon: Film, color: 'text-[var(--rq-blue)]' },
  asset_update: { icon: Film, color: 'text-sky-300' },
  asset_delete: { icon: Film, color: 'text-red-400' },
  folder_create: { icon: FolderOpen, color: 'text-[var(--rq-blue)]' },
  folder_update: { icon: FolderOpen, color: 'text-sky-300' },
  folder_delete: { icon: FolderOpen, color: 'text-red-400' },
  rate_limit_exceeded: { icon: AlertTriangle, color: 'text-red-400' },
}

// ---------------------------------------------------------------------------
// Mini bar chart (activity per day)
// ---------------------------------------------------------------------------

function ActivityChart({ data }: { data: { day: string; count: number }[] }) {
  const max = Math.max(...data.map(d => d.count), 1)
  return (
    <div className="flex items-end gap-[3px] h-16">
      {data.map((d, i) => {
        const pct = Math.max((d.count / max) * 100, 4)
        return (
          <div
            key={i}
            className="flex-1 rounded-sm bg-[var(--rq-blue)] hover:bg-[var(--rq-blue)]/80 transition-colors min-w-[3px]"
            style={{ height: `${pct}%` }}
            title={`${d.day}: ${d.count} action${d.count !== 1 ? 's' : ''}`}
          />
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SystemPage() {
  const [usage, setUsage] = useState<CreditsUsage | null>(null)
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState(30)

  // Memoised so the effect below can depend on it honestly. Declared
  // inline it was a new function every render, so adding it to the
  // dependency array would have re-fetched on every render instead of
  // when `period` changes.
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [creditsRes, dashRes] = await Promise.allSettled([
        apiClient.get('/system/credits'),
        apiClient.get('/system/dashboard', { params: { days: period } }),
      ])
      if (creditsRes.status === 'fulfilled') setUsage(creditsRes.value.data)
      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value.data)
      if (creditsRes.status === 'rejected' && dashRes.status === 'rejected') {
        setError('Failed to load system data')
      }
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => { fetchAll() }, [fetchAll])

  const totalActions = useMemo(
    () => Object.values(dashboard?.activity_by_type || {}).reduce((s, c) => s + c, 0),
    [dashboard],
  )

  return (
    <div className="rq-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="rq-title">System</h1>
          <p className="rq-subtitle">{dashboard?.is_admin ? 'System-wide overview' : 'Your workspace at a glance'}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={e => setPeriod(Number(e.target.value))}
            className="rq-input-dark rounded-lg px-3 py-1.5 text-xs"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="rq-btn-ghost px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {error && (
        <div className="rq-card p-4 border border-red-500/40 bg-red-500/10 mb-6">
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      {/* ---- Top stats row ---- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard
          label="Workspaces"
          value={dashboard?.counts.projects}
          icon={FolderOpen}
          color="text-[var(--rq-blue)]"
        />
        <StatCard
          label="Assets"
          value={dashboard?.counts.assets}
          icon={Film}
          color="text-[var(--rq-amber)]"
        />
        <StatCard
          label="Moments"
          value={dashboard?.counts.moments}
          icon={Scissors}
          color="text-emerald-400"
        />
        <StatCard
          label="Assemblies"
          value={dashboard?.counts.assemblies}
          icon={Sparkles}
          color="text-purple-400"
        />
        <StatCard
          label="Footage"
          value={dashboard ? formatDuration(dashboard.counts.total_footage_seconds) : undefined}
          icon={Clock}
          color="text-sky-300"
          raw
        />
        <StatCard
          label="Actions"
          value={totalActions || undefined}
          icon={Activity}
          color="text-[var(--rq-text)]"
        />
      </div>

      {/* ---- Two-column: Activity chart + AI Drafts ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Activity chart */}
        <div className="rq-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-[var(--rq-blue)]" />
            <p className="text-xs font-semibold text-[var(--rq-text-soft)] uppercase tracking-wider">Daily Activity</p>
          </div>
          {dashboard && dashboard.activity_per_day.length > 0 ? (
            <ActivityChart data={dashboard.activity_per_day} />
          ) : (
            <div className="h-16 flex items-center justify-center text-xs text-[var(--rq-text-soft)]">
              {loading ? 'Loading...' : 'No activity yet'}
            </div>
          )}
          {/* Type breakdown below chart */}
          {dashboard && Object.keys(dashboard.activity_by_type).length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 pt-3 border-t border-white/[0.06]">
              {Object.entries(dashboard.activity_by_type)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => {
                  const meta = ACTIVITY_ICONS[type] || { icon: Activity, color: 'text-[var(--rq-text-soft)]' }
                  const Icon = meta.icon
                  return (
                    <span key={type} className="inline-flex items-center gap-1 text-[11px] text-[var(--rq-text-soft)]">
                      <Icon className={`w-3 h-3 ${meta.color}`} />
                      <span className="text-[var(--rq-text)] tabular-nums">{count}</span>
                      {type.replace(/_/g, ' ')}
                    </span>
                  )
                })}
            </div>
          )}
        </div>

        {/* AI Drafts */}
        <div className="rq-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[var(--rq-amber)]" />
            <p className="text-xs font-semibold text-[var(--rq-text-soft)] uppercase tracking-wider">AI Drafts</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-2xl font-semibold text-[var(--rq-text)] tabular-nums">
                {usage?.ai_drafts_total ?? '—'}
              </p>
              <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5">Total created</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-[var(--rq-text)] tabular-nums">
                {usage?.ai_drafts_last_7d ?? '—'}
              </p>
              <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5">Last 7 days</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--rq-amber)] animate-pulse" />
              <div>
                <p className="text-sm font-semibold text-[var(--rq-text)] tabular-nums">
                  {usage?.ai_drafts_generating ?? '—'}
                </p>
                <p className="text-[10px] text-[var(--rq-text-soft)]">Generating</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-sm font-semibold text-[var(--rq-text)] tabular-nums">
                  {usage?.ai_drafts_ready ?? '—'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-sm font-semibold text-[var(--rq-text)] tabular-nums">
                  {usage?.ai_drafts_failed ?? '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Activity Feed ---- */}
      <div className="rq-card overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06]">
          <Activity className="w-4 h-4 text-[var(--rq-text-soft)]" />
          <p className="text-xs font-semibold text-[var(--rq-text-soft)] uppercase tracking-wider">Recent Activity</p>
          {dashboard && (
            <span className="ml-auto text-[10px] text-[var(--rq-text-soft)]">
              Last {dashboard.period_days} days
            </span>
          )}
        </div>
        {loading && !dashboard ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--rq-text-soft)]" />
          </div>
        ) : dashboard && dashboard.activity_feed.length > 0 ? (
          <div className="divide-y divide-white/[0.04]">
            {dashboard.activity_feed.map(a => {
              const meta = ACTIVITY_ICONS[a.type] || { icon: Activity, color: 'text-[var(--rq-text-soft)]' }
              const Icon = meta.icon
              return (
                <div key={a.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-white/[0.02] transition-colors">
                  <div className={`flex-shrink-0 p-1.5 rounded-lg bg-white/5 ${meta.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--rq-text)] truncate">{a.description}</p>
                    <p className="text-[10px] text-[var(--rq-text-soft)]">
                      {a.type.replace(/_/g, ' ')}{a.user_email ? ` · ${a.user_email}` : ''}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-[10px] text-[var(--rq-text-soft)] tabular-nums">
                    {formatRelativeTime(a.created_at)}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="py-12 text-center text-xs text-[var(--rq-text-soft)]">
            No activity recorded yet. Start by uploading assets.
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  raw,
}: {
  label: string
  value: number | string | undefined
  icon: typeof Activity
  color: string
  raw?: boolean
}) {
  return (
    <div className="rq-card px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <p className="text-[10px] text-[var(--rq-text-soft)] uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-xl font-semibold text-[var(--rq-text)] tabular-nums">
        {value !== undefined ? (raw ? value : typeof value === 'number' ? value.toLocaleString() : value) : '—'}
      </p>
    </div>
  )
}
